import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import styles from './SchedulerSetting.module.scss';
import {
  getOnboardingSettings,
  getOnboardingDocuments,
  getOnboardingTemplateTypes,
  deleteOnboardingDocument,
} from '../../../core/API/onboarding';
import {
  IOnboardingSettings,
  IOnboardingDocument,
  IOnboardingTemplateType,
} from 'core/interfaces/onboardingInterface';
import { Partner } from 'types';
import { getPartners } from 'core/API/partners';
import EditGeneralSettings from './Onboarding/EditGeneralSettings';
import DocumentsList from './Onboarding/DocumentsList';
import UploadDocument from './Onboarding/UploadDocument';
import EditDocument from './Onboarding/EditDocument';

type ViewMode =
  | 'list'
  | 'editGeneralSettings'
  | 'uploadDocument'
  | 'editDocument';

const OnboardingSetting = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [settings, setSettings] = useState<IOnboardingSettings | null>(null);
  const [documents, setDocuments] = useState<IOnboardingDocument[]>([]);
  const [templateTypes, setTemplateTypes] = useState<IOnboardingTemplateType[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [editingDocument, setEditingDocument] = useState<IOnboardingDocument | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<IOnboardingDocument | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const workplaceId = Number(localStorage.getItem('workplace-id'));
    const [settingsData, documentsData, typesData, partnersData] = await Promise.all([
      getOnboardingSettings(workplaceId),
      getOnboardingDocuments(workplaceId),
      getOnboardingTemplateTypes(),
      getPartners({ active: true }),
    ]);
    setSettings(settingsData);
    setDocuments(
      (documentsData || [])
        .filter((d) => d.active !== false)
        .sort((a, b) => a.sortOrder - b.sortOrder)
    );
    setTemplateTypes(typesData || []);
    setPartners(partnersData || []);
    setViewMode('list');
  };

  const getPartnerLabel = (storageProvider: string | undefined): string => {
    if (!storageProvider) return 'Not configured';
    
    const partner = partners.find(
      (p) => (p['@id'] === storageProvider || `/api/partners/${p.id}` === storageProvider)
    );
    
    return partner?.label || storageProvider;
  };

  const handleBackClick = () => {
    setViewMode('list');
    setEditingDocument(null);
  };

  const handleDeleteClick = (document: IOnboardingDocument) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteOnboardingDocument(documentToDelete.id);
      await loadData();
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  // Render edit views
  if (viewMode === 'editGeneralSettings') {
    return (
      <div className={styles.schedulerSettings}>
        <EditGeneralSettings
          settings={settings}
          handleBackClick={handleBackClick}
          onSettingsUpdated={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'uploadDocument') {
    return (
      <div className={styles.schedulerSettings}>
        <UploadDocument
          existingDocumentsCount={documents.length}
          existingNames={documents.map((d) => d.name)}
          templateTypes={templateTypes}
          handleBackClick={handleBackClick}
          onDocumentUploaded={loadData}
        />
      </div>
    );
  }

  if (viewMode === 'editDocument' && editingDocument) {
    return (
      <div className={styles.schedulerSettings}>
        <EditDocument
          document={editingDocument}
          existingNames={documents.map((d) => d.name).filter((name) => name !== editingDocument.name)}
          handleBackClick={handleBackClick}
          onDocumentUpdated={loadData}
        />
      </div>
    );
  }

  // Main list view
  return (
    <div className={styles.schedulerSettings}>
      {/* General Settings (EIN + Storage Provider) */}
      <Card className="card">
        <div className={styles.header}>
          <h2 className={styles.formTitle}>General Settings</h2>
        </div>
        <div className={styles.list}>
          <div className={styles.tableContainer}>
            <table className={styles.workplaceTable}>
              <thead>
                <tr>
                  <th>Employer Identification Number (EIN)</th>
                  <th>Storage Provider</th>
                  <th style={{ width: 48, textAlign: 'center' }}>Edit</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <span>{settings?.ein || 'Not configured'}</span>
                  </td>
                  <td>
                    <span>
                      {getPartnerLabel(settings?.storageProvider)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
                    <button
                      className={styles.iconButton}
                      title="Edit"
                      onClick={() => {
                        setViewMode('editGeneralSettings');
                      }}
                    >
                      <Edit className={styles.editIcon} />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Onboarding Documents */}
      <Card className="card">
        <div className={styles.header}>
          <h2 className={styles.formTitle}>
            Onboarding Documents ({documents.length}/50)
          </h2>
          <Button
            className={styles.addButton}
            onClick={() => setViewMode('uploadDocument')}
            disabled={documents.length >= 50}
          >
            Add
          </Button>
        </div>
        <div className={styles.list}>
          <DocumentsList
            documents={documents}
            templateTypes={templateTypes}
            onEditClick={(doc) => {
              setEditingDocument(doc);
              setViewMode('editDocument');
            }}
            onDeleteClick={handleDeleteClick}
          />
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{documentToDelete?.name}&quot;?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button className={styles.mabelButton} onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button className={styles.mabelButton} onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OnboardingSetting;
